import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import Modal from './Modal'
import { PrimaryButton, SecondaryButton } from './Button'
import Dropdown, { DropdownContext, DropdownItem } from './Dropdown'
import LoadingBar from './LoadingBar'
import ConvertController from '../state/ConvertController'
import Radom, { ISwapQuoteResponse } from '../state/Radom'
import Treasury from '../state/Treasury'
import User from '../state/User'
import { ManagedPaymentMethod, getMethod, getPaymentMethods } from '../util/Managed'
import { ISwapSupportedAsset, isSwapSupportedAsset } from '../util/OkxSwap'
import { errorToast, formatAssetAmount, successToast, useTestMode } from '../util/Util'
import { usePermissions } from '../util/permissions'

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const AssetCard = styled.div`
  background: var(--surface-subtle, #fbfcfe);
  border: 1px solid var(--border-soft, #eceff3);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
`

const CardLabelRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
`

const CardLabel = styled.span`
  color: var(--text-muted, #6b7280);
  font-size: 11.5px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`

const CardMeta = styled.button`
  background: none;
  border: 0;
  border-radius: 6px;
  color: var(--text-muted, #6b7280);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12.25px;
  padding: 0;
  transition: color 0.16s ease;

  &:hover {
    color: var(--ink, #20242d);
  }
`

const AmountRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
`

const AmountInput = styled.input`
  background: transparent;
  border: none;
  caret-color: var(--ink, #20242d);
  color: var(--ink-strong, #121722);
  flex: 1;
  font-family: var(--font-sans);
  font-size: 26px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  line-height: 1.1;
  min-width: 90px;
  outline: none;
  padding: 0;

  &::placeholder {
    color: var(--text-disabled, #a4aab5);
  }
`

const EstimateValue = styled.span`
  color: var(--ink-strong, #121722);
  font-size: 26px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  line-height: 1.1;
`

const FlipRow = styled.div`
  display: flex;
  justify-content: center;
  margin: -4px 0;
  position: relative;
  z-index: 1;
`

const FlipButton = styled.button`
  align-items: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e3e7ef);
  border-radius: 999px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  color: var(--text-muted, #6b7280);
  cursor: pointer;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  transition: border-color 0.16s ease, color 0.16s ease, transform 0.18s ease;
  width: 34px;

  &:hover {
    border-color: var(--border-strong, #c9d0dc);
    color: var(--ink, #20242d);
    transform: rotate(180deg);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`

const QuotePanel = styled.div`
  background: var(--surface-muted, #f8fafc);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 12px 14px;
`

const QuoteLine = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
`

const QuoteKey = styled.span`
  color: var(--text-muted, #6b7280);
  font-size: 12.75px;
`

const QuoteVal = styled.span`
  color: var(--ink, #20242d);
  font-size: 12.75px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  text-align: right;
`

const HintNote = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 12px;
  line-height: 1.45;
`

const MethodOption = styled.span`
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
`

const MethodName = styled.span`
  color: var(--ink, #20242d);
  font-size: 13.5px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const methodKey = (m: ManagedPaymentMethod): string => `${m.name}:${m.ticker}`

/** Chain + token address the swap API addresses this method by. */
const toPair = (m: ManagedPaymentMethod): { network: string, token?: string } => ({
  network: m.hostChain?.name ?? m.name,
  token: m.hostChain?.tokenAddress
})

const renderMethod = (m?: ManagedPaymentMethod, placeholder = 'Select asset'): ReactElement => m
  ? <MethodOption>
    <img src={m.logo} style={{ height: 20, maxWidth: 20, width: 'auto' }} />
    <MethodName>{m.ticker}</MethodName>
  </MethodOption>
  : <span style={{ color: 'var(--icon-muted, #747894)', fontSize: 13.5 }}>{placeholder}</span>

/**
 * The global Convert drawer: opens from any Convert button via
 * ConvertController without navigating away from the current page.
 */
const ConvertDrawer = observer((): ReactElement => {
  const [testMode] = useTestMode()
  const { has } = usePermissions()

  const [supportedAssets, setSupportedAssets] = useState<ISwapSupportedAsset[]>([])
  const [fromKey, setFromKey] = useState<string>()
  const [toKey, setToKey] = useState<string>()
  const [amount, setAmount] = useState('')
  const [quote, setQuote] = useState<ISwapQuoteResponse | null>(null)
  const [isQuoting, setIsQuoting] = useState(false)
  const [quoteError, setQuoteError] = useState('')
  const [step, setStep] = useState<'compose' | 'confirm'>('compose')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const amountRef = useRef<HTMLInputElement | null>(null)
  const quoteSeq = useRef(0)

  const canWriteSwap = has('WriteSwap')
  const swapEnabled = User.organization?.features?.isSwapEnabled === true
  const unifiedTreasury = User.organization?.features?.isUnifiedTreasuryEnabled === true

  /** Swappable methods in the current mode, with balances where held. Unified
   *  orgs hold one pot per asset, so the picker offers each asset once. */
  const methods = useMemo(() => getPaymentMethods()
    .filter(m => m.isSwapEnabled && !m.isFiat && !m.isHidden && !m.isDisabled)
    .filter(m => testMode ? !!m.isTestnet : !m.isTestnet)
    .filter(m =>
      supportedAssets.length === 0 ||
      isSwapSupportedAsset(toPair(m).network, toPair(m).token ?? null, supportedAssets))
    .filter((m, index, list) => {
      if (!unifiedTreasury) return true
      // Unified orgs pick assets, not networks, but the conversion request
      // still names one variant. Prefer the Ethereum instrument so the
      // legacy shadow ledger debits the network deposits usually live on.
      const variants = list.filter(candidate => candidate.ticker === m.ticker)
      const preferred = variants.find(v => toPair(v).network === 'Ethereum') ?? variants[0]
      return m === preferred
    })
  , [testMode, supportedAssets, unifiedTreasury])

  const balanceFor = (m?: ManagedPaymentMethod): number => {
    if (!m) return 0
    return Treasury.managedBalances
      .filter(b => !b.treasuryId)
      .filter(b => {
        // Unified-treasury rows carry an asset ticker and no network; the
        // unified balance funds a convert from any network, so match by
        // ticker. They only ever hold mainnet funds.
        if (b.asset) {
          return !testMode && b.asset.toLowerCase() === m.ticker.toLowerCase()
        }
        if (b.network === undefined) return false
        const method = getMethod(b.network, b.token)
        // A testnet row must never read as a live balance (or the other way
        // round): the picker is already scoped to the current mode.
        if (testMode ? !method.isTestnet : !!method.isTestnet) return false
        return method.name === m.name && method.ticker === m.ticker
      })
      .reduce((sum, b) => sum + Number(b.balance), 0)
  }

  const fromMethod = methods.find(m => methodKey(m) === fromKey)
  const toMethod = methods.find(m => methodKey(m) === toKey)
  const fromBalance = balanceFor(fromMethod)

  // Load balances and the venue's supported assets when the drawer opens,
  // and preselect the largest holdable source (or the caller's preference).
  useEffect(() => {
    if (!ConvertController.isOpen) return

    setStep('compose')
    setAmount('')
    setQuote(null)
    setQuoteError('')

    if (has('ReadBalance')) {
      void Treasury.loadBalances()
    }
    Radom.getSwapSupportedAssets()
      .then(response => setSupportedAssets(response.assets ?? []))
      .catch(() => setSupportedAssets([]))

    window.setTimeout(() => amountRef.current?.focus(), 80)
  }, [ConvertController.isOpen])

  useEffect(() => {
    if (!ConvertController.isOpen || methods.length === 0) return

    const preferred = ConvertController.preferredTicker &&
      methods.find(m => m.ticker === ConvertController.preferredTicker && balanceFor(m) > 0)
    const richest = [...methods].sort((a, b) => balanceFor(b) - balanceFor(a))[0]
    const source = preferred || richest
    if (source && !fromKey) {
      setFromKey(methodKey(source))
    }
  }, [ConvertController.isOpen, methods.length, Treasury.managedBalances.length])

  const requestQuote = async (
    nextAmount: string,
    from?: ManagedPaymentMethod,
    into?: ManagedPaymentMethod
  ): Promise<void> => {
    const value = Number(nextAmount)
    if (!from || !into || !Number.isFinite(value) || value <= 0) {
      setQuote(null)
      setQuoteError('')
      return
    }

    const seq = ++quoteSeq.current
    setIsQuoting(true)
    setQuoteError('')
    try {
      const response = await Radom.getSwapRates({
        from: toPair(from),
        into: toPair(into),
        amount: value
      })
      if (seq === quoteSeq.current) {
        setQuote(response)
      }
    } catch (err) {
      if (seq === quoteSeq.current) {
        setQuote(null)
        setQuoteError(err.response?.data?.error || 'Couldn’t fetch a rate for this pair')
      }
    } finally {
      if (seq === quoteSeq.current) {
        setIsQuoting(false)
      }
    }
  }

  const submit = async (): Promise<void> => {
    if (!fromMethod || !toMethod || isSubmitting) return
    setIsSubmitting(true)
    try {
      await Radom.initiateSwap({
        from: toPair(fromMethod),
        into: toPair(toMethod),
        amount: Number(amount)
      })
      successToast('Conversion submitted')
      void Treasury.loadBalances()
      ConvertController.close()
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsSubmitting(false)
  }

  const receiveAmount = quote ? Number(quote.amount) : 0
  const parsedAmount = Number(amount)
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0 && parsedAmount <= fromBalance
  const canReview = amountValid && !!fromMethod && !!toMethod && !!quote && !isQuoting

  const renderAssetPicker = (
    selected: ManagedPaymentMethod | undefined,
    exclude: ManagedPaymentMethod | undefined,
    onPick: (m: ManagedPaymentMethod) => void
  ): ReactElement => (
    <Dropdown
      preferredAlignment='right'
      overflow='auto'
      outerStyle={{ minHeight: 38, minWidth: 128, padding: '7px 10px', borderRadius: 10 }}
      innerStyle={{ maxHeight: 300, width: 240 }}
      selectedContent={renderMethod(selected)}
      dropdownContent={<DropdownContext.Consumer>
        {({ closeDropdown }) => <div>
          {methods
            .filter(m => !exclude || methodKey(m) !== methodKey(exclude))
            .map(m => <DropdownItem
              key={methodKey(m)}
              style={{
                alignItems: 'center',
                display: 'flex',
                gap: 10,
                justifyContent: 'space-between',
                padding: '10px 12px'
              }}
              onClick={() => {
                closeDropdown()
                onPick(m)
              }}>
              {renderMethod(m)}
              <span style={{ color: 'var(--text-faint, #8a919e)', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                {formatAssetAmount(balanceFor(m), m.ticker)}
              </span>
            </DropdownItem>)}
        </div>}
      </DropdownContext.Consumer>} />
  )

  const renderUnavailable = (): ReactElement => (
    <Composer>
      <HintNote style={{ fontSize: 13 }}>
        {swapEnabled
          ? 'Your role can’t create conversions. Ask an organization admin for the WriteSwap permission.'
          : 'Conversions aren’t enabled for your organization yet. Contact support to switch them on.'}
      </HintNote>
      <SecondaryButton onClick={ConvertController.close} style={{ alignSelf: 'flex-start' }}>Close</SecondaryButton>
    </Composer>
  )

  const renderCompose = (): ReactElement => (
    <Composer>
      <AssetCard>
        <CardLabelRow>
          <CardLabel>You convert</CardLabel>
          {fromMethod && <CardMeta
            type='button'
            title='Use full balance'
            onClick={() => {
              const max = String(fromBalance)
              setAmount(max)
              void requestQuote(max, fromMethod, toMethod)
            }}>
            Balance: {formatAssetAmount(fromBalance, fromMethod.ticker)} {fromMethod.ticker}
          </CardMeta>}
        </CardLabelRow>
        <AmountRow>
          <AmountInput
            ref={amountRef}
            value={amount}
            inputMode='decimal'
            placeholder='0'
            aria-label='Amount to convert'
            onChange={e => {
              const raw = e.target.value
              if (!/^\d*\.?\d*$/.test(raw)) return
              setAmount(raw)
              void requestQuote(raw, fromMethod, toMethod)
            }} />
          {renderAssetPicker(fromMethod, toMethod, m => {
            setFromKey(methodKey(m))
            void requestQuote(amount, m, toMethod)
          })}
        </AmountRow>
        {amount !== '' && !amountValid && fromMethod &&
          <HintNote style={{ color: 'var(--pill-red-ink, #c51735)' }}>
            {parsedAmount > fromBalance ? 'More than your available balance' : 'Enter an amount above zero'}
          </HintNote>}
      </AssetCard>

      <FlipRow>
        <FlipButton
          type='button'
          aria-label='Swap direction'
          disabled={!fromMethod || !toMethod}
          onClick={() => {
            if (!fromMethod || !toMethod) return
            setFromKey(methodKey(toMethod))
            setToKey(methodKey(fromMethod))
            setAmount('')
            setQuote(null)
          }}>
          ⇅
        </FlipButton>
      </FlipRow>

      <AssetCard>
        <CardLabelRow>
          <CardLabel>You receive</CardLabel>
        </CardLabelRow>
        <AmountRow>
          {isQuoting
            ? <LoadingBar style={{ borderRadius: 8, height: 30, maxWidth: 140 }} />
            : <EstimateValue>
              {quote && amountValid ? formatAssetAmount(receiveAmount, toMethod?.ticker) : '--'}
            </EstimateValue>}
          {renderAssetPicker(toMethod, fromMethod, m => {
            setToKey(methodKey(m))
            void requestQuote(amount, fromMethod, m)
          })}
        </AmountRow>
      </AssetCard>

      <QuotePanel>
        {quoteError
          ? <HintNote style={{ color: 'var(--pill-red-ink, #c51735)' }}>{quoteError}</HintNote>
          : quote && amountValid && fromMethod && toMethod
            ? <>
              <QuoteLine>
                <QuoteKey>Rate</QuoteKey>
                <QuoteVal>
                  1 {fromMethod.ticker} ≈ {formatAssetAmount(Number(quote.rate), toMethod.ticker)} {toMethod.ticker}
                </QuoteVal>
              </QuoteLine>
              <HintNote>All fees are included in the rate.</HintNote>
            </>
            : <QuoteLine>
              <QuoteKey>Rate</QuoteKey>
              <QuoteVal>--</QuoteVal>
            </QuoteLine>}
      </QuotePanel>

      <PrimaryButton
        disabled={!canReview}
        style={{ width: '100%' }}
        onClick={() => setStep('confirm')}>
        Review conversion
      </PrimaryButton>
    </Composer>
  )

  const renderConfirm = (): ReactElement => (
    <Composer>
      <QuotePanel style={{ gap: 10, padding: '16px 14px' }}>
        <QuoteLine>
          <QuoteKey>You convert</QuoteKey>
          <QuoteVal>{formatAssetAmount(parsedAmount, fromMethod?.ticker)} {fromMethod?.ticker}</QuoteVal>
        </QuoteLine>
        <QuoteLine>
          <QuoteKey>You receive</QuoteKey>
          <QuoteVal>≈ {formatAssetAmount(receiveAmount, toMethod?.ticker)} {toMethod?.ticker}</QuoteVal>
        </QuoteLine>
        <QuoteLine>
          <QuoteKey>Rate</QuoteKey>
          <QuoteVal>
            1 {fromMethod?.ticker} ≈ {formatAssetAmount(Number(quote?.rate), toMethod?.ticker)} {toMethod?.ticker}
          </QuoteVal>
        </QuoteLine>
      </QuotePanel>
      <HintNote>
        The final amount can differ slightly from the estimate if the market moves while the
        conversion settles. All fees are included in the rate.
      </HintNote>
      <div style={{ display: 'flex', gap: 8 }}>
        <SecondaryButton style={{ flex: 1 }} onClick={() => setStep('compose')}>Back</SecondaryButton>
        <PrimaryButton style={{ flex: 2 }} isLoading={isSubmitting} onClick={() => { void submit() }}>
          Convert now
        </PrimaryButton>
      </div>
    </Composer>
  )

  return <Modal
    visible={ConvertController.isOpen}
    onClose={ConvertController.close}
    presentation='drawer'
    title='Convert'>
    {(!swapEnabled || !canWriteSwap)
      ? renderUnavailable()
      : step === 'compose' ? renderCompose() : renderConfirm()}
  </Modal>
})

export default ConvertDrawer
